import React from 'react';

interface AgentStatusBarProps {
  isRunning: boolean;
  isInterrupting?: boolean;

  // Working text - can be a string, React node, or function
  workingText?: string | React.ReactNode | ((isRunning: boolean, isInterrupting: boolean) => React.ReactNode);

  // Legacy prop for backwards compatibility
  statusText?: string | ((isRunning: boolean, isInterrupting: boolean) => string);

  showInterruptHint?: boolean;
  className?: string;

  // For custom status component
  renderStatus?: (props: {
    isRunning: boolean;
    isInterrupting: boolean;
  }) => React.ReactNode;
}

export const AgentStatusBar: React.FC<AgentStatusBarProps> = ({
  isRunning,
  isInterrupting = false,
  workingText,
  statusText,
  showInterruptHint = true,
  className = '',
  renderStatus,
}) => {
  if (!isRunning) return null;

  // Use custom render if provided
  if (renderStatus) {
    return (
      <div className={className}>
        {renderStatus({ isRunning, isInterrupting })}
      </div>
    );
  }

  // Get the working text to display
  const getWorkingText = () => {
    // Priority: workingText > statusText > default
    if (workingText !== undefined) {
      if (typeof workingText === 'function') {
        return workingText(isRunning, isInterrupting);
      }
      return workingText;
    }

    if (typeof statusText === 'function') {
      return statusText(isRunning, isInterrupting);
    }
    if (statusText) {
      return statusText;
    }

    return 'Vibe is thinking...';
  };

  return (
    <div className={`px-4 py-2 flex-shrink-0 ${className}`}>
      <div className="flex items-center gap-2">
        <span
          className="inline-block animate-pulse"
          style={{ color: 'rgb(109, 76, 153)' }}
        >
          ○
        </span>
        <span className="text-sm text-gray-700">
          {getWorkingText()}
          {showInterruptHint && (
            <span className="ml-2 text-xs text-gray-400">
              {isInterrupting ? '(interrupting...)' : '(esc to interrupt)'}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};
